import "features/messages/src/styles/MessageInput/index.scss";
import clsx from "clsx";
import { useMemo, useRef } from "react";
import {
  EditMessageForm,
  isOnlyEmojis,
  MESSAGE_ACTIONS,
  MessageActions,
  MessageDeleted,
  MessageInput,
  MessageRepliesCountButton,
  MessageStatus,
  MessageTimestamp,
  ReactionIcon,
  ReactionsList,
  ReactionSelector,
  renderText as defaultRenderText,
  showMessageActionsBox,
  ThreadIcon,
  useComponentContext,
  useMessageContext,
  useTranslationContext,
  DialogAnchor,
  useDialog,
  useDialogIsOpen,
} from "stream-chat-react";
import styled from "styled-components";

import PinIndicator from "./PinIndicator";

import UserBadge from "../UserBadge/UserBadge";
import { useWorkspaceController } from "../../context/WorkspaceController";

import ErrorIcon from "./icons";

const StyledSpan = styled.span`
  font-size: 11px;
  color: var(--text-low-emphasis-color);
`;

const TeamMetaSpacer = styled.div`
  width: 34px;
`;

export default function TeamMessage() {
  const {
    clearEditingState,
    editing,
    getMessageActions,
    groupStyles,
    handleAction,
    handleOpenThread: handleOpenThreadContext,
    handleRetry,
    initialMessage,
    message,
    onMentionsClickMessage,
    onUserClick,
    renderText = defaultRenderText,
    threadList,
  } = useMessageContext("MessageTeam");
  const { Attachment } = useComponentContext("MessageTeam");

  const { t, userLanguage } = useTranslationContext("MessageTeam");

  const messageActions = getMessageActions();
  const showActionsBox = showMessageActionsBox(messageActions);

  const shouldShowReplies = messageActions.indexOf(MESSAGE_ACTIONS.reply) > -1 && !threadList;
  const canReact = messageActions.indexOf(MESSAGE_ACTIONS.react) > -1;

  const messageTextToRender = message.i18n?.[`${userLanguage}_text`] || message.text;

  const messageMentionedUsersItem = message.mentioned_users;

  const messageText = useMemo(
    () => renderText(messageTextToRender, messageMentionedUsersItem),
    [messageMentionedUsersItem, messageTextToRender, renderText],
  );

  const { closePinnedMessageListOpen } = useWorkspaceController();
  const handleOpenThread = (event) => {
    closePinnedMessageListOpen();
    handleOpenThreadContext(event);
  };

  const firstGroupStyle = groupStyles ? groupStyles[0] : "single";

  const buttonRef = useRef(null);
  const reactionSelectorDialogId = `reaction-selector--${message.id}`;
  const reactionSelectorDialog = useDialog({ id: reactionSelectorDialogId });
  const reactionSelectorDialogIsOpen = useDialogIsOpen(reactionSelectorDialogId);
  const messageActionsDialogIsOpen = useDialogIsOpen(`message-actions--${message.id}`);

  if (message.deleted_at) {
    return <MessageDeleted message={message} />;
  }

  if (editing) {
    return (
      <div
        className={`str-chat__message-team str-chat__message-team--${firstGroupStyle} str-chat__message-team--editing`}
        data-testid="message-team-edit"
      >
        {(firstGroupStyle === "top" || firstGroupStyle === "single") && (
          <div className="str-chat__message-team-meta">
            <UserBadge
              image={message.user?.image}
              name={message.user?.name || message.user?.id}
              userId={message.user?.id}
              onClick={onUserClick}
              size={34}
            />
          </div>
        )}
        <MessageInput
          clearEditingState={clearEditingState}
          Input={EditMessageForm}
          message={message}
        />
      </div>
    );
  }
  const rootClass = clsx(
    "str-chat__message",
    "str-chat__message-team",
    `str-chat__message-team--${firstGroupStyle}`,
    {
      "pinned-message": message.pinned,
      [`str-chat__message-team--${message.status}`]: message.status,
      [`str-chat__message-team--${message.type}`]: message.type,
      "str-chat__message--has-attachment": !!message.attachments?.length,
      threadList,
    },
  );

  return (
    <div className={message.pinned ? "pinned-message" : "unpinned-message"}>
      {message.pinned && <PinIndicator message={message} />}
      <div className={rootClass} data-testid="message-team">
        <div className="avatar-host">
          {firstGroupStyle === "top" || firstGroupStyle === "single" || initialMessage ? (
            <UserBadge
              image={message.user?.image}
              name={message.user?.name || message.user?.id}
              userId={message.user?.id}
              onClick={onUserClick}
              size={34}
            />
          ) : (
            <TeamMetaSpacer data-testid="team-meta-spacer" />
          )}
        </div>
        <div className="str-chat__message-team-group">
          {(firstGroupStyle === "top" || firstGroupStyle === "single" || initialMessage) && (
            <div className="str-chat__message-team-meta">
              <div
                className="str-chat__message-team-author"
                data-testid="message-team-author"
                onClick={onUserClick}
                onKeyDown={onUserClick}
                tabIndex={0}
                role="button"
              >
                <strong>{message.user?.name || message.user?.id}</strong>
                {message.type === "error" && (
                  <div className="str-chat__message-team-error-header">
                    {String(t("Only visible to you"))}
                  </div>
                )}
              </div>
              <MessageTimestamp />
            </div>
          )}
          <div
            className={`str-chat__message-team-content str-chat__message-team-content--${firstGroupStyle} str-chat__message-team-content--${
              message.text === "" ? "image" : "text"
            }`}
            data-testid="message-team-content"
          >
            {!initialMessage &&
              message.status !== "sending" &&
              message.status !== "failed" &&
              message.type !== "system" &&
              message.type !== "ephemeral" &&
              message.type !== "error" && (
                <div
                  className={clsx(`str-chat__message-team-actions`, {
                    "str-chat__message-team-actions--active":
                      reactionSelectorDialogIsOpen || messageActionsDialogIsOpen,
                  })}
                  data-testid="message-team-actions"
                >
                  {canReact && (
                    <>
                      <DialogAnchor
                        id={reactionSelectorDialogId}
                        placement="top-end"
                        referenceElement={buttonRef.current}
                        trapFocus
                      >
                        <ReactionSelector />
                      </DialogAnchor>
                      <button
                        aria-expanded={reactionSelectorDialogIsOpen}
                        aria-label={String(t("aria/Open Reaction Selector"))}
                        className="str-chat__message-reactions-button"
                        data-testid="message-reaction-action"
                        onClick={() => reactionSelectorDialog?.toggle()}
                        ref={buttonRef}
                        type="button"
                      >
                        <ReactionIcon className="str-chat__message-action-icon" />
                      </button>
                    </>
                  )}
                  {shouldShowReplies && (
                    <span
                      data-testid="message-team-thread-icon"
                      onClick={handleOpenThread}
                      onKeyDown={handleOpenThread}
                      tabIndex={0}
                      role="button"
                      title="Start a thread"
                    >
                      <ThreadIcon />
                    </span>
                  )}
                  {showActionsBox && <MessageActions inline />}
                </div>
              )}
            {message.text && (
              <div
                className={clsx("str-chat__message-team-text", {
                  "str-chat__message-team-text--is-emoji": isOnlyEmojis(message.text),
                })}
                data-testid="message-team-message"
                onClick={onMentionsClickMessage}
                onKeyDown={onMentionsClickMessage}
                role="button"
                tabIndex={0}
              >
                {messageText}
                {message?.debtorData && (
                  <StyledSpan>
                    Sent from{" "}
                    <a href={`/debtors/${message?.debtorData?.id}`}>
                      {message?.debtorData?.name ??
                        `debtor ${message?.debtorData?.externalId || message?.debtorData?.id}`}
                    </a>
                    's profile.
                  </StyledSpan>
                )}
              </div>
            )}
            {!message.text && message.attachments?.length && Attachment ? (
              <Attachment actionHandler={handleAction} attachments={message.attachments} />
            ) : null}
            {message.latest_reactions?.length !== 0 && message.text !== "" && canReact && (
              <ReactionsList />
            )}
            {message.status === "failed" && (
              <button
                className="str-chat__message-team-failed"
                data-testid="message-team-failed"
                onClick={message.errorStatusCode !== 403 ? () => handleRetry(message) : undefined}
                type="button"
              >
                <ErrorIcon />
                {message.errorStatusCode !== 403
                  ? String(t("Message Failed · Click to try again"))
                  : String(t("Message Failed · Unauthorized"))}
              </button>
            )}
          </div>
          <MessageStatus messageType="team" />
          {message.text && message.attachments?.length && Attachment ? (
            <Attachment actionHandler={handleAction} attachments={message.attachments} />
          ) : null}
          {message.latest_reactions &&
            message.latest_reactions.length !== 0 &&
            message.text === "" &&
            canReact && <ReactionsList />}
          {!threadList && (
            <MessageRepliesCountButton
              onClick={handleOpenThread}
              reply_count={message.reply_count}
            />
          )}
        </div>
      </div>
    </div>
  );
}
